import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  type DeletedBoard,
  useDeletedBoards,
  usePurgeDeletedBoard,
  useRestoreDeletedBoard,
} from '@/entities/board';
import { useViewer } from '@/entities/viewer';
import { FormError } from '@/shared/forms';
import { formatDateTime, timeLeft } from '@/shared/lib/datetime';
import { Button, ConfirmDialog, Dialog, ErrorState, LoadingState } from '@/shared/ui';

import styles from './Recovery.module.css';

/**
 * Boards deleted from the workspace (kept 7 days). Restoring brings the board back as it was;
 * what had been deleted inside it earlier stays in the board's own Trash.
 */
export function WorkspaceTrashDialog({
  workspaceId,
  onClose,
}: {
  workspaceId: string;
  onClose: () => void;
}) {
  const { t, i18n } = useTranslation();
  const viewer = useViewer();
  const timeZone = viewer.status === 'authenticated' ? viewer.user.timezone : undefined;
  const deleted = useDeletedBoards(workspaceId, true);
  const restore = useRestoreDeletedBoard(workspaceId);
  const purge = usePurgeDeletedBoard(workspaceId);
  const [purging, setPurging] = useState<DeletedBoard | null>(null);

  if (purging) {
    return (
      <ConfirmDialog
        title={t('workspaceTrash.purgeTitle')}
        message={t('workspaceTrash.purgeMessage', { name: purging.name })}
        confirmLabel={t('trash.purge')}
        pending={purge.isPending}
        onCancel={() => {
          setPurging(null);
        }}
        onConfirm={() => {
          purge.mutate(purging.id, {
            onSettled: () => {
              setPurging(null);
            },
          });
        }}
      />
    );
  }

  const purgeText = (board: DeletedBoard) => {
    const { days, hours } = timeLeft(board.purge_after);
    const left =
      days > 0
        ? t('trash.inDays', { count: days })
        : hours > 0
          ? t('trash.inHours', { count: hours })
          : t('trash.soon');
    return t('trash.purgeAt', {
      left,
      date: formatDateTime(board.purge_after, i18n.language, timeZone),
    });
  };

  return (
    <Dialog title={t('workspaceTrash.title')} onClose={onClose} size="lg">
      <p className={styles.hint}>{t('workspaceTrash.hint')}</p>
      <FormError error={restore.error ?? purge.error} />
      {deleted.isPending && <LoadingState />}
      {deleted.isError && (
        <ErrorState error={deleted.error} onRetry={() => void deleted.refetch()} />
      )}
      {deleted.data?.length === 0 && <p className={styles.empty}>{t('workspaceTrash.empty')}</p>}
      {deleted.data && deleted.data.length > 0 && (
        <ul className={styles.list} aria-label={t('workspaceTrash.title')}>
          {deleted.data.map((board) => (
            <li key={board.id} className={styles.item}>
              <div className={styles.itemText}>
                <span className={styles.itemTitle}>{board.name}</span>
                <span className={styles.itemMeta}>
                  {t('trash.deletedAt', {
                    when: formatDateTime(board.trashed_at, i18n.language, timeZone),
                  })}
                </span>
                <span className={styles.itemMeta}>{purgeText(board)}</span>
              </div>
              <div className={styles.itemActions}>
                <Button
                  size="sm"
                  disabled={restore.isPending}
                  aria-label={t('trash.restoreOne', { title: board.name })}
                  onClick={() => {
                    restore.mutate(board.id);
                  }}
                >
                  {t('trash.restore')}
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  className={styles.danger}
                  aria-label={t('trash.purgeOne', { title: board.name })}
                  onClick={() => {
                    setPurging(board);
                  }}
                >
                  {t('trash.purge')}
                </Button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </Dialog>
  );
}
